import React, {ChangeEvent, useEffect, useState} from "react";
import {Mail} from "../../models/Mail";
import {Document} from "../../models/Document";
import {MetaObject} from "../../models/MetaObject";
import {Alert, Backdrop, Box, Button, CircularProgress, MenuItem, Stack, TextField} from "@mui/material";
import {translateFunction} from "../../../../localization/TranslationService";
import {getPresignedUrl} from "../document/Document";
import {IMailAddress, MailAddressField} from "../../../../components/common/input/MailAddressField";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_MAIL_TARGETS} from "../graphql/queries/gqlGetMailTargets";
import {GlobalState} from "../../../../global/GlobalState";
import {ChipsField} from "../../../../components/common/input/ChipsField";
import {DocumentFile} from "../../models/DocumentFile";
import {IMailDocumentInput} from "../../models/MailDocumentInput";
import {MAIL_DOCUMENT} from "../graphql/mutations/gqlMailDocument";
import {MAILFROM_ADDRESSES} from "../graphql/mutations/gqlGetMailFromAddresses";
import {toast} from "react-toastify";
import SendIcon from "@mui/icons-material/Send";
import {mailDocumentType} from "../document/DocumentActionsCard";
import {GET_MAILTEMPLATES} from "../graphql/queries/gqlGetMailTemplates";
import {DropdownMenu} from "../../../../components/common/DropdownMenu";
import AddIcon from "@mui/icons-material/Add";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "../../../../libraries/ckeditor/ckeditor";
import {DocumentFileType} from "../../models/DocumentFileType";
import {AttachFile} from "@mui/icons-material";
import {GET_PRESIGNED_URL_UPLOAD} from "../graphql/queries/gqlGetPresignedUrlUpload";
import {IPresignedUrlUploadPayload} from "../../models/PresignedUrlUploadPayload";
import moment from "moment";

const translate = translateFunction("SureBaseDocuments");

interface IMailDocumentCardProps {
	document?: Document;
	onClose?: () => void;
	reloadDocument?: () => void;
	mailDocumentType: mailDocumentType;
}

interface IMailTemplate {
	id: number;
	type: string;
	name: string;
	content: string;
}

export const MailDocumentCard: React.FC<IMailDocumentCardProps> = (props) => {
	const [mail, setMail] = useState<Mail>({documentId: 0});
	const [showCc, setShowCc] = useState(false);
	const [showBcc, setShowBcc] = useState(false);
	const [body, setBody] = useState("");
	const [attachments, setAttachments] = useState<DocumentFile[]>([]);
	const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
	const [mailAddresses, setMailAddresses] = useState<IMailAddress[]>([]);
	const [fromMailAddresses, setFromMailAddresses] = useState<string[]>([]);
	const [mailTemplates, setMailTemplates] = useState<IMailTemplate[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [bodyLoading, setBodyLoading] = useState<boolean>(false);
	
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [subjectError, setSubjectError] = useState<string>("");
	
	const fetchMailFromAddresses = async () => {
		const result = await graphQLServices.documentsClient.query({
			query: MAILFROM_ADDRESSES,
		});
		setFromMailAddresses(result.data.mailFromAddresses);
		
	};
	const fetchTemplates = async () => {
		const result = await graphQLServices.documentsClient.query({
			query: GET_MAILTEMPLATES,
			variables: {
				type: "SYSTEM"
			}
		});
		
		const templates = result.data.mailTemplates.items.map((target: any) => {
			const mailTemplate: IMailTemplate = {
				id: target.id,
				type: target.type,
				name: target.name,
				content: target.content
			};
			return mailTemplate;
		});
		setMailTemplates(templates);
	};
	
	const fetchUserSignature = async () => {
		
		const result = await graphQLServices.documentsClient.query({
			query: GET_MAILTEMPLATES,
			variables: {
				type: "USER",
				where: {"name": {"in": [GlobalState.userSession?.user.emailAddress, "default"]}}
			}
		});
		
		if (result.data.mailTemplates.items.length === 0)
			return null;
		let content: string = result.data.mailTemplates.items.find((x: IMailTemplate) => x.name === GlobalState.userSession?.user.emailAddress)?.content
			?? result.data.mailTemplates.items.find((x: IMailTemplate) => x.name === "default")?.content;
		
		content = content.replace("%USER.NAME%", GlobalState.userSession?.user.name ?? "")
			.replace("%USER.EMAIL%", GlobalState.userSession?.user.emailAddress ?? "");
		
		return content;
	};
	
	async function getPresignedUrlUpload(fileName: string): Promise<IPresignedUrlUploadPayload> {
		let result: IPresignedUrlUploadPayload = {key: "", url: "", contentType: ""};
		await graphQLServices.documentsClient.query({
			query: GET_PRESIGNED_URL_UPLOAD,
			variables: {
				fileName
			},
			fetchPolicy: "network-only",
		})
			.then(value => result = value.data.filePresignedUrlUpload as IPresignedUrlUploadPayload);
		return result;
	}
	
	const fetchMailAddresses = async (id: string, from?: string, to?: string, replyTo?: string) => {
		const result = await graphQLServices.documentsClient.query({
			query: GET_MAIL_TARGETS,
			variables: {
				id: id,
			},
		});
		let addresses: IMailAddress[] = [];
		if (!replyTo && from) {
			addresses = [...addresses, {group: "Mail", name: translate("From"), mail: from}];
		}
		if (replyTo) {
			addresses = [...addresses, {group: "Mail", name: translate("From"), mail: replyTo}];
		}
		if (to) {
			addresses = [...addresses, {group: "Mail", name: translate("To"), mail: to}];
		}
		
		addresses = [...addresses, ...result.data.mailTargets.map((target: any) => {
			const mailTarget: IMailAddress = {group: translate(target.source), mail: target.email, name: target.name};
			return mailTarget;
		})];
		
		setMailAddresses(addresses);
		
	};
	
	const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files) {
			return;
		}
		const file = event.target.files[0];
		
		const upload = await getPresignedUrlUpload(file.name);
		const myHeaders = new Headers({
			"Content-Type": upload.contentType,
		});
		setSubmitLoading(true);
		fetch(upload.url, {
			method: "PUT",
			body: file,
			headers: myHeaders
		}).then(() => {
			setAttachments([...attachments, {
				id: 0,
				groupId: 0,
				parentGroupId: 0,
				deleted: false,
				type: DocumentFileType.Attachment,
				name: file.name,
				fileKey: upload.key,
				skipExtraction: true,
				contentCheckSum: null,
				textCheckSum: null,
				extension: null,
				timeStamp: Date.now().toString()
			}]
			);
			setSelectedAttachments([...selectedAttachments, file.name]);
			return;
		}).catch((_) => {
			toast.error(translate(`Upload failed`));
		}).finally(() => {
			setSubmitLoading(false);
		});
		
	};
	
	function createFileFromString(content: string, fileName: string, mimeType: string): File {
		const blob = new Blob([content], { type: mimeType });
		return new File([blob], fileName, { type: mimeType });
	}
	
	async function mailDocument() {
		if (!props.document?.files)
			return;

		// Validate subject field
		if (!mail.subject || mail.subject.trim() === "") {
			setSubjectError(translate("Subject is required"));
			toast.error(translate("Subject is required"));
			return;
		}
		setSubjectError("");
		const input: IMailDocumentInput = {
			documentIdentifier: props.document.identifier,
			from: mail.from,
			to: mail.to,
			cc: mail.cc,
			bcc: mail.bcc,
			subject: mail.subject,
			attachments: attachments.filter(x => selectedAttachments.indexOf(x.name) > -1).map(a => {
				return {id: a.id, name: a.name, fileKey: a.fileKey};
			}),
		};
		setSubmitLoading(true);
		setErrorMessage("");
		
		let failed = false;
		setSubmitLoading(true);
		
		// Max ALB Payload is 1 Mb so 700Kb is the limit for body to leave some room for the metadata.
		if (body.length > 0.7 * 1024 * 1024) {
			const upload = await getPresignedUrlUpload("Body.html");
			const myHeaders = new Headers({
				"Content-Type": upload.contentType,
			});
			
			const file = createFileFromString(body, "Body.hml", upload.contentType);
			input.bodyKey = upload.key;
			await fetch(upload.url, {
				method: "PUT",
				body: file,
				headers: myHeaders
			}).catch((_) => {
				failed = true;
				toast.error(translate("Failed to upload the body"));
				setSubmitLoading(false);
			});
		} else {
			input.body = body;
		}
		
		if (!failed) {
			await graphQLServices.documentsClient.mutate({
				mutation: MAIL_DOCUMENT,
				variables: {input: input}
			})
				.then(() => {
					toast.success(`Mail succesfully sent.`);
					props.reloadDocument && props.reloadDocument();
					props.onClose && props.onClose();
				})
				.then(() => {
					if (errorMessage === "") {
						props.onClose && props.onClose();
					}
				})
				.catch((reason) => {
					toast.error(translate(reason.networkError.result?.errors[0]?.extensions?.message ?? reason.networkError.result.errors[0].message));
				})
				.finally(() => {
					setSubmitLoading(false);
				});
		}
	}
	
	function htmlEnc(s: string | null | undefined) {
		if (s === null || s === undefined)
			return undefined;
		return s.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/'/g, "&#39;")
			.replace(/"/g, "&#34;");
	}

	// Outlook frequently emits invalid list markup, e.g. a <ul> whose <li> items
	// are wrapped in a stray <div>, or bare <p> elements placed directly inside a
	// <ul>/<ol>. CKEditor's list schema only accepts <li> as a direct child of a
	// list, so it silently drops everything else — this is what made forwarded/
	// replied bodies lose bullet points and paragraphs. GeneralHtmlSupport keeps
	// unknown tags/attributes but cannot fix this structural mismatch, so we
	// normalize the markup before it reaches the editor: unwrap non-<li> wrappers
	// that contain list items, and lift non-list blocks out to after the list.
	function normalizeListsForEditor(htmlString: string): string {
		if (typeof window === "undefined" || typeof window.DOMParser === "undefined")
			return htmlString;
		if (htmlString.indexOf("<ul") === -1 && htmlString.indexOf("<ol") === -1)
			return htmlString;

		const doc = new window.DOMParser().parseFromString(htmlString, "text/html");

		const unwrap = (el: Element) => {
			const parent = el.parentNode;
			if (!parent)
				return;
			while (el.firstChild)
				parent.insertBefore(el.firstChild, el);
			parent.removeChild(el);
		};

		let changed = true;
		let guard = 0;
		while (changed && guard++ < 50) {
			changed = false;
			doc.querySelectorAll("ul, ol").forEach((list) => {
				let anchor: Node = list;
				Array.from(list.children).forEach((child) => {
					if (child.tagName === "LI")
						return;
					if (child.querySelector && child.querySelector("li")) {
						// Wrapper (e.g. <div>) around the actual list items: unwrap it
						// so the <li>'s become direct children of the list.
						unwrap(child);
						changed = true;
					} else {
						// Non-list block (e.g. <p>) sitting inside the list: move it out
						// to just after the list, preserving document order.
						if (list.parentNode) {
							list.parentNode.insertBefore(child, anchor.nextSibling);
							anchor = child;
							changed = true;
						}
					}
				});
			});
		}

		return doc.body.innerHTML;
	}
	
	useEffect(() => {
		const extractMail = async (document: Document) => {
			const metaObject = new MetaObject(document?.metaData);
			const bodyId = document.files?.filter((it) => it.name === "Body.html").firstOrNull()?.id;
			
			const signature = await fetchUserSignature();
			
			let bodyText = "";
			let subject = htmlEnc(metaObject.mailSubject ?? "");
			let to: string[] = [];
			
			if (bodyId) {
				setBodyLoading(true);
				const presignedUrlResult = await getPresignedUrl(bodyId, document.id, false);
				const data = await fetch(presignedUrlResult.url);
				bodyText = await data.text();
				// Remove Style Sections because ckEditor will remove the tags and inner styles will be added as normal textnpm st
				bodyText = bodyText.replace(/<style([\S\s]*?)>([\S\s]*?)<\/style>/gi, "");
				// Repair Outlook's invalid list markup so CKEditor keeps every bullet/paragraph.
				bodyText = normalizeListsForEditor(bodyText);
				let header = "";
				header += "From: " + htmlEnc(metaObject.mailSender) + "<br>";
				header += "Date: " + moment(document.created).format("DD-MM-YYYY HH:mm:ss") + "<br>";
				header += "Subject: " + htmlEnc(metaObject.mailSubject) + "<br>";
				
				if (metaObject.mailReplyTo && metaObject.mailReplyTo.length > 0) {
					header += "ReplyTo: " + metaObject.mailReplyTo?.map((s) => htmlEnc(s)).join(";") + "<br>";
				}
				
				if (metaObject.mailTo && metaObject.mailTo.length > 0) {
					header += "To: " + metaObject.mailTo?.map((s) => htmlEnc(s)).join(";") + "<br>";
				}
				
				if (metaObject.mailCc && metaObject.mailCc.length > 0) {
					header += "Cc: " + metaObject.mailCc?.map((s) => htmlEnc(s)).join(";") + "<br>";
				}
				
				const replyTo = metaObject.mailReplyTo?.length > 0 ? metaObject.mailReplyTo.filter(x => x !== undefined).map(x => x as string) : (metaObject.mailSender ? [metaObject.mailSender] : []);
				switch (props.mailDocumentType) {
					case mailDocumentType.Reply:
						subject = "RE: " + subject;
						to = replyTo;
						bodyText = "<blockquote>---------- Replied message ---------<br>" + header + "<br><br>" + bodyText + "</blockquote>";
						break;
					case mailDocumentType.Forward:
						subject = "FW: " + subject;
						bodyText = "<blockquote>---------- Forwarded message ---------<br>" + header + "<br><br>" + bodyText + "</blockquote>";
						break;
				}
			}
			
			setBody(signature + bodyText);
			setBodyLoading(false);
			setMail({
				...mail,
				documentId: document.id,
				subject: subject,
				from: GlobalState.userSession?.user.emailAddress ?? "",
				to: to
			});
			setAttachments(props.document?.files?.filter((it) => it.type === DocumentFileType.Attachment && (it.parentGroupId ?? 0) === 0) ?? []);
			setSelectedAttachments((props.document?.files?.filter((it) => it.type === DocumentFileType.Attachment && (it.parentGroupId ?? 0) === 0) ?? []).map((it) => it.name));
		};
		if (props.document && mail.documentId !== props.document.id) {
			setLoading(true);
			const metaObject = new MetaObject(props.document.metaData);
			fetchMailAddresses(props.document.identifier, metaObject.mailSender, metaObject.mailTo.first(), metaObject.mailReplyTo.firstOrNull() ?? undefined)
				.then(() => props.document && extractMail(props.document))
				.then(() => fetchMailFromAddresses())
				.then(() => fetchTemplates())
				.finally(() => setLoading(false));
		}
		
	}, [props.document, mail, props.mailDocumentType]);
	
	if (loading)
		return <CircularProgress/>;
	
	
	return <>
		<Backdrop open={submitLoading}><CircularProgress/></Backdrop>
		{errorMessage &&
			<Alert severity={"error"}>{JSON.stringify(errorMessage)}</Alert>
		}
		<Box sx={{marginBottom: 2}} justifyContent="space-between" flexDirection={"column"}>
			<Button key={"sendButton"} endIcon={<SendIcon/>} variant={"contained"}
				onClick={mailDocument}>{translate("Send")}</Button>
		</Box>
		<Stack spacing={1}>
			
			<TextField
				key={"From"}
				label={translate("From")}
				value={mail.from}
				onChange={(event) => setMail({...mail, from: event.target.value})}
				InputLabelProps={{shrink: true}}
				select
			>
				{GlobalState.userSession?.user.emailAddress && <MenuItem
					value={GlobalState.userSession?.user.emailAddress}>{GlobalState.userSession?.user.emailAddress}</MenuItem>}
				{fromMailAddresses.map((a, i) =>
					<MenuItem key={i} value={a}>{a}</MenuItem>
				)}
			</TextField>
			<MailAddressField
				key={"to"}
				grouped={true}
				label={translate("To")}
				options={mailAddresses}
				value={mail.to}
				onValueChange={(values) => {
					setMail({...mail, to: values?.map((v) => v)});
				}}
				endAdornment={<>
					{!showCc && <Button variant="text" onClick={() => setShowCc(true)}>Cc</Button>}
					{!showBcc && <Button variant="text" onClick={() => setShowBcc(true)}>Bcc</Button>}
				</>}
			/>
			{showCc &&
				<MailAddressField
					key={"cc"}
					label={translate("Cc")}
					options={mailAddresses}
					value={mail.cc}
					onValueChange={(values) => {
						setMail({...mail, cc: values?.map((v) => v)});
					}}
				/>
			}
			{showBcc &&
				<MailAddressField
					key={"bcc"}
					label={translate("Bcc")}
					options={mailAddresses}
					value={mail.bcc}
					onValueChange={(values) => {
						setMail({...mail, bcc: values?.map((v) => v)});
					}}
				/>
			}
			<TextField key={"Subject"} label={translate("Subject")} required value={mail?.subject ?? ""}
				onChange={(event) => {
					setMail({...mail, subject: event.target.value});
					// Clear error when user starts typing
					if (subjectError) {
						setSubjectError("");
					}
				}}
				InputLabelProps={{shrink: true}}
				error={!!subjectError}
				helperText={subjectError}/>
			<ChipsField<DocumentFile>
				key={"attachments"}
				optionValue={option => typeof option === "string" ? option : option.name}
				optionLabel={option => typeof option === "string" ? option : option.name}
				label={translate("Attachments")}
				options={attachments}
				value={selectedAttachments}
				onValueChange={(values) => {
					setSelectedAttachments(values ?? []);
				}}
				endAdornment={<>
					{<Button variant="text" component="label">
						<AttachFile/>
						<input
							type="file"
							hidden
							onChange={handleFileUpload}
						/>
					
					</Button>}
				</>}
			/>
			{bodyLoading &&
				<CircularProgress/>
			}
			{!bodyLoading &&
				<div
					style={{
						maxHeight: "100%",
						height: "100%",
						width: "100%",
						position: "relative",
						overflow: "scroll",
					}}
				>
					<DropdownMenu label={<><AddIcon></AddIcon>Templates</>}>
						{
							mailTemplates.map(value => {
								return <MenuItem key={value.id}
									onClick={() => setBody(value.content + body)}>{value.name}</MenuItem>;
							})
						}
					</DropdownMenu>
					<CKEditor
						key={"body"}
						editor={ClassicEditor}
						data={body}
						onBlur={(_, editor) => {
							setBody(editor.getData());
						}}
					/>
				
				</div>
			}
		
		</Stack>
	
	</>;
};
